/**
 * Parent Options Hook
 *
 * Builds a list of selectable parent options (workspace + projects)
 * from search results. Shared by CommandPalette and CommandPaletteCombobox
 * to eliminate duplicate option-building logic.
 */

import { useMemo } from 'react';
import type { ParentOption } from '../../components/features/search/parentOptions.types';
import type { SearchResult } from '../../services/search.service';
import type { Workspace } from '../../contexts/WorkspaceContext';

export function useParentOptions(
  workspace: Workspace | null,
  searchResults: SearchResult[],
  currentParentId?: string,
  query?: string,
  includeWorkspace = true,
): { options: ParentOption[]; filteredOptions: ParentOption[] } {
  const options = useMemo(() => {
    const items: ParentOption[] = [];

    // Add workspace option first (always valid for both project and artifact).
    // Callers targeting a project specifically (e.g. connector import) pass
    // includeWorkspace=false to drop it.
    if (includeWorkspace && workspace) {
      items.push({
        id: workspace.id,
        name: workspace.name,
        type: 'workspace',
      });
    }

    // Add project results from search, excluding currentParentId
    for (const result of searchResults) {
      if (result.id !== currentParentId) {
        items.push({
          id: result.id,
          name: result.title,
          type: 'project',
          // Carried through verbatim — the destination row renders the same
          // breadcrumb the search row would for this collection.
          location: result.location,
        });
      }
    }

    return items;
  }, [workspace, searchResults, currentParentId, includeWorkspace]);

  // Client-side filter for workspace option when typing
  const filteredOptions = useMemo(() => {
    if (!query || query.trim().length === 0) return options;
    const lower = query.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(lower));
  }, [options, query]);

  return { options, filteredOptions };
}
